import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { Scope, ScopesListResponse } from '../../lib/types.js';

const SCOPES_QUERY_KEY = queryKeys.scopes();

/**
 * Grant pickers default to scopes supported by the tenant's packages.
 * The super-admin catalog page explicitly requests the complete catalog.
 */
export const useScopesLogic = (availableOnly = true) => {
  const query = useQuery<ScopesListResponse>({
    queryKey: availableOnly ? [...SCOPES_QUERY_KEY, 'available'] : SCOPES_QUERY_KEY,
    queryFn: () =>
      apiRequest<ScopesListResponse>(availableOnly ? '/api/scopes?available=true' : '/api/scopes'),
  });

  return {
    isLoading: query.isLoading,
    scopes: query.data?.scopes ?? [],
    error: query.error,
  };
};

/** Create-only fields. `key` is the immutable identifier principals reference. */
export interface CreateScopeInput {
  key: string;
  displayName: string;
  description: string;
}

/** `key` is intentionally omitted — renaming it would orphan every assignment. */
export type UpdateScopeInput = Pick<CreateScopeInput, 'displayName' | 'description'>;

export const useCreateScopeLogic = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<Scope, Error, CreateScopeInput>({
    mutationFn: (input) => apiRequest<Scope>('/api/scopes', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCOPES_QUERY_KEY });
      onSuccess();
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    create: async (input: CreateScopeInput): Promise<void> => {
      await mutation.mutateAsync(input);
    },
    reset: () => mutation.reset(),
  };
};

export const useUpdateScopeLogic = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<Scope, Error, { scopeId: string; input: UpdateScopeInput }>({
    mutationFn: ({ scopeId, input }) =>
      apiRequest<Scope>(`/api/scopes/${scopeId}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCOPES_QUERY_KEY });
      onSuccess();
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    update: async (scopeId: string, input: UpdateScopeInput): Promise<void> => {
      await mutation.mutateAsync({ scopeId, input });
    },
    reset: () => mutation.reset(),
  };
};

export const useDeleteScopeLogic = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation<void, Error, string>({
    mutationFn: (scopeId) => apiRequest<void>(`/api/scopes/${scopeId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCOPES_QUERY_KEY });
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    remove: async (scopeId: string): Promise<void> => {
      await mutation.mutateAsync(scopeId);
    },
  };
};
